import { shortDateTime, clientTime } from '../../shared/dateTime';
import { plural } from '../../shared/plural';

/**
 * Warianty według makiety back office v2, fala A (decyzja użytkownika
 * 20.10.2026, D1) — front tylko pokazuje to, co policzyło API
 * (`user-offers/variant-validity.ts`, `variant-conflicts.ts`):
 *
 * - termin ważności wariantu, „po terminie” i „wygasa”, kto ustawił termin
 *   i czy pytający może go zmienić (`validity` przy wariancie karty);
 * - konflikt w czasie: inne zlecenia, w których to auto jest wariantem albo
 *   obsadzeniem w nachodzącym czasie (`timeConflicts`);
 * - znaczniki wiersza banku (`variantMarkers`) i dwie wartości filtra
 *   „Znaczniki”.
 *
 * **Po terminie wariant nie zwalnia się sam** — zwalnia autor albo Team
 * Leader. Reguły tu nie ma; brak pól (przełącznik wyłączony) = ekran jak
 * dotąd.
 */

/** Klucze przełączników — jak w API (`feature-flags.ts`), nie przemianowujemy. */
export const FEATURE_VARIANT_VALIDITY = 'variantValidity';
export const FEATURE_ASSIGN_BUTTON_FOR_PLANNER = 'assignButtonForPlanner';
export const FEATURE_RECOMMENDATION_SNAPSHOT = 'recommendationSnapshot';

/**
 * Wartości filtra „Znaczniki” (klucz `forklift`) — kontrakt z API
 * (`MARKERS_FILTER_VARIANT_CONFLICT`, `MARKERS_FILTER_VARIANT_EXPIRED`
 * w `user-offers/filters.ts`). React-admin je zapamiętuje — to dane.
 */
export const MARKERS_FILTER_VARIANT_CONFLICT = 'variantConflict';
export const MARKERS_FILTER_VARIANT_EXPIRED = 'variantExpired';

export const VARIANT_MARKER_CHOICES = [
  { id: MARKERS_FILTER_VARIANT_CONFLICT, name: 'Konflikt wariantów w czasie' },
  { id: MARKERS_FILTER_VARIANT_EXPIRED, name: 'Wariant po terminie' },
];

/** Opcje filtra z dwiema nowymi wartościami — tylko z przełącznikiem. */
export function withVariantMarkerChoices<C extends { id: string; name: string }>(
  choices: readonly C[],
  on: boolean,
): Array<C | { id: string; name: string }> {
  return on ? [...choices, ...VARIANT_MARKER_CHOICES] : [...choices];
}

/** Podpowiedź przy nieaktywnym „Obsadź” u planisty (makieta: „Obsadza Master FTL”). */
export const TEAM_LEADER_ASSIGNS_HINT =
  'Obsadza Team Leader — Ty wskazujesz pojazd jako wariant';

export interface VariantValidity {
  /** ISO; `null` = wariant bez terminu. */
  validUntil: string | null;
  setBy: string | null;
  setAt: string | null;
  expired: boolean;
  expiresSoon: boolean;
  canChange: boolean;
}

export interface TimeConflict {
  userOfferId: number;
  externalId: string | null;
  /** `variant` | `assignment` — dane API. */
  kind: string;
}

type Loose = Record<string, unknown>;

const iso = (v: unknown): string | null =>
  typeof v === 'string' && v !== '' ? v : null;

/** Termin wariantu z odpowiedzi API; `null`, gdy go nie ma (przełącznik). */
export function variantValidityOf(candidacy: Loose | undefined): VariantValidity | null {
  const raw = candidacy?.validity as Loose | null | undefined;
  if (!raw || typeof raw !== 'object') return null;
  return {
    validUntil: iso(raw.validUntil),
    setBy: typeof raw.setBy === 'string' ? raw.setBy : null,
    setAt: iso(raw.setAt),
    expired: raw.expired === true,
    expiresSoon: raw.expiresSoon === true,
    canChange: raw.canChange === true,
  };
}

function conflictList(raw: unknown): TimeConflict[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((c): c is Loose => !!c && typeof c === 'object')
    .filter((c) => typeof c.userOfferId === 'number')
    .map((c) => ({
      userOfferId: c.userOfferId as number,
      externalId: typeof c.externalId === 'string' ? c.externalId : null,
      kind: typeof c.kind === 'string' ? c.kind : 'variant',
    }));
}

/** Konflikty w czasie przy wariancie karty. */
export function timeConflictsOf(candidacy: Loose | undefined): TimeConflict[] {
  return conflictList(candidacy?.timeConflicts);
}

/** Znaczniki wiersza banku; `null` bez przełącznika. */
export function variantMarkersOf(
  record: Loose,
): { expired: number; conflicts: TimeConflict[] } | null {
  const raw = record.variantMarkers as Loose | null | undefined;
  if (!raw || typeof raw !== 'object') return null;
  return {
    expired: typeof raw.expired === 'number' ? raw.expired : 0,
    conflicts: conflictList(raw.conflicts),
  };
}

/** „ważny do 21.10, 17:00” / „po terminie (od 20.10, 17:00)” / „bez terminu”. */
export function validityText(v: VariantValidity): string {
  if (!v.validUntil) return 'bez terminu ważności';
  if (v.expired) return `po terminie (od ${shortDateTime(v.validUntil)})`;
  if (v.expiresSoon) return `wygasa o ${clientTime(v.validUntil)}`;
  return `ważny do ${shortDateTime(v.validUntil)}`;
}

/** Dymek terminu: kto ustawił i co dalej po terminie. */
export function validityHint(v: VariantValidity): string {
  const who = v.setBy ? `Termin ustawił: ${v.setBy}${v.setAt ? `, ${shortDateTime(v.setAt)}` : ''}.` : '';
  const after = v.expired
    ? ' Wariant nie zwalnia się sam — zwalnia go autor albo Team Leader, albo zmieniają termin.'
    : ' Po terminie wariant zostaje z oznaczeniem „po terminie”; zwalnia go autor albo Team Leader.';
  return `${who}${after}`.trim();
}

const orderLabel = (c: TimeConflict) => c.externalId ?? `#${c.userOfferId}`;

/** „w tym samym czasie: wariant PL/2, obsadzenie PL/3”. */
export function conflictsText(conflicts: readonly TimeConflict[]): string {
  const parts = conflicts.map(
    (c) => `${c.kind === 'assignment' ? 'obsadzenie' : 'wariant'} ${orderLabel(c)}`,
  );
  return `konflikt w czasie: ${parts.join(', ')}`;
}

/** Dymek znacznika wiersza banku. */
export function markersHint(markers: { expired: number; conflicts: TimeConflict[] }): {
  expired: string | null;
  conflict: string | null;
} {
  return {
    expired:
      markers.expired > 0
        ? `${plural(markers.expired, 'wariant jest', 'warianty są', 'wariantów jest')} po terminie ważności. Nic nie zwalnia się samo — zwalnia autor albo Team Leader.`
        : null,
    conflict:
      markers.conflicts.length > 0
        ? `Ten sam pojazd w nachodzącym czasie przy: ${markers.conflicts
            .map((c) => `${orderLabel(c)} (${c.kind === 'assignment' ? 'obsadzenie' : 'wariant'})`)
            .join(', ')}. To ostrzeżenie, nie blokada.`
        : null,
  };
}
